import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { currentBranding } from '../../../config/branding';
import {
  connectorTypes,
  desiredPowerOutputUnits,
  deviceModelSeries,
  maxPowerOutputUnits,
} from '../../../config/enum';
import { DeviceDetailsEdit } from '../../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllStationsList } from '../../../redux/slice/ApprovedStationManagementSlice';
import { fetchAllDevices } from '../../../redux/slice/DeviceManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomInputSelectBox from '../../../shared-components/custom-input-select-box/CustomInputSelectBox';
import CustomInputSwitch from '../../../shared-components/custom-input-switch/CustomInputSwitch';
import FormCancelWarning from '../../../shared-components/form-cancel-warning/FormCancelWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotificaion';
import { floatingNumberValidation, stringValidation } from '../../../shared-functions/Validation';
import deviceApi from '../../../utils/api/device';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * EditDevice component handles the creation of new devices in the device management system.
 * It provides a form interface for users to input device details such as name, ID, and power specifications.
 * The component includes form validation, submission handling, and navigation controls.
 * @returns {JSX.Element} A form interface for adding new devices
 */
function EditDevice() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const { allStations, loading } = useAppSelector((state) => state.approvedStation);
  const { page, limit, devices } = useAppSelector((states) => states.device);

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    buttonLoader: false,
  });

  const oldDevice = devices.find((device) => device.id === id);

  const [deviceDetails, setDeviceDetails] = useState<DeviceDetailsEdit>({
    chargingStation: oldDevice?.chargingStationId || '',
    chargePointModel: oldDevice?.chargePointModel || '',
    chargingPointSn: oldDevice?.chargingPointSn || '',
    chargingPointName: oldDevice?.chargingPointName || '',
    connectorType: oldDevice?.connectorType || '',
    maximumOutputPower: oldDevice?.maximumOutputPower || 0,
    maximumOutputPowerUnit: oldDevice?.maximumOutputPowerUnit || 'kW',
    powerUnit: oldDevice?.chargeRate?.unit || 'W',
    desiredPowerOutput:
      oldDevice?.chargeRate?.unit === 'W'
        ? parseFloat(oldDevice?.chargeRate?.value?.toString() || '0') / 1000
        : oldDevice?.chargeRate?.value || 0,
    isJuiceBoxPncEnabled: oldDevice?.isJuiceBoxPncEnabled || false,
    modelSeries: oldDevice?.modelSeries || '',
    isInVLBC: oldDevice?.isInVLBC ?? false,
    isDeviceOffline: oldDevice?.isDeviceOffline ?? false,
    useTxDefaultProfile: oldDevice?.useTxDefaultProfile ?? false,
  });

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
  });

  /**
   * Fetch the all station data function
   */
  const fetchData = () => {
    if (!loading) {
      dispatch(fetchAllStationsList());
    }
  };

  /**
   * Updates the device details state when form fields change.
   * @param {string} name - The name of the field to update
   * @param {string | number} changeValue - The new value for the field
   */
  const handleDeviceDetailChange = (name: string, changeValue: string | number | boolean) => {
    setDeviceDetails({
      ...deviceDetails,
      [name]: changeValue,
    });
  };

  /**
   * Resets the form state and navigates back to the device management page.
   * This is called after successful submission or when user confirms cancellation.
   */
  const handleNavigateBack = () => {
    setDeviceDetails({
      chargingStation: '',
      chargingPointName: '',
      chargingPointSn: '',
      chargePointModel: '',
      connectorType: '',
      maximumOutputPower: 0,
      maximumOutputPowerUnit: 'kW',
      desiredPowerOutput: 0,
      powerUnit: 'W',
      isJuiceBoxPncEnabled: false,
      modelSeries: '',
      isInVLBC: false,
      isDeviceOffline: false,
      useTxDefaultProfile: false,
    });
    setModalBoxStatus('close');

    navigate('../');
  };

  /**
   * Displays a warning modal when user attempts to cancel form submission.
   * Gives users the option to confirm cancellation or continue editing.
   */
  const handleClickCancel = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <FormCancelWarning
          title="Are you sure want to Cancel?"
          description="Any unsaved changes will be loss"
          cancelButtonLabel="Cancel"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitButtonLabel="OK"
          submitButtonOnClick={handleNavigateBack}
        />
      ),
      notificationModal: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Handles the submission of device details.
   * Validates required fields, creates a FormData object with device details,
   * and makes an API call to add the station. Shows success notification on completion.
   */
  const handleSubmitDeviceDetails = () => {
    if (
      id &&
      deviceDetails.chargingStation &&
      stringValidation(1, 50, deviceDetails.chargePointModel) &&
      deviceDetails.connectorType &&
      ((deviceDetails.maximumOutputPower && deviceDetails.maximumOutputPower > 0) ||
        deviceDetails.maximumOutputPower < 999)
    ) {
      setFormStatus({
        showFormErrors: false,
        buttonLoader: true,
      });
      const request: any = {
        chargePointModel: deviceDetails.chargePointModel,
        chargingPointName: deviceDetails.chargingPointName,
        chargingPointSn: deviceDetails.chargingPointSn,
        chargingStation: deviceDetails.chargingStation,
        connectorType: deviceDetails.connectorType,
        maximumOutputPower: deviceDetails.maximumOutputPower,
        maximumOutputPowerUnit: deviceDetails.maximumOutputPowerUnit,
        chargeRate: {
          unit: deviceDetails.powerUnit,
          value: deviceDetails.desiredPowerOutput,
        },
        isJuiceBoxPncEnabled: deviceDetails.isJuiceBoxPncEnabled,
        useTxDefaultProfile: deviceDetails.useTxDefaultProfile,
        modelSeries: deviceDetails.modelSeries,
      };
      if (deviceDetails.powerUnit === 'W') {
        request.chargeRate.value = String(deviceDetails.desiredPowerOutput * 1000);
      }
      if (deviceDetails.maximumOutputPowerUnit === 'W') {
        request.maximumOutputPower = String(deviceDetails.maximumOutputPower * 1000);
      }
      deviceApi
        .editDevice(request, id)
        .then(() => {
          dispatch(fetchAllDevices({ page, limit }));
          setFormStatus({
            showFormErrors: false,
            buttonLoader: false,
          });
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title="Device Updated Successfully "
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={handleNavigateBack}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: true,
            buttonLoader: false,
          });
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    } else {
      setFormStatus({
        showFormErrors: true,
        buttonLoader: false,
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="add-device-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="add-device-header">
        <PageTitle title="Edit Device" />
      </div>
      <div className="add-device-container">
        <div className="add-device-from-wrap">
          <div className="add-device-from">
            <SelectFiled
              inputLabel="Station Name"
              name="chargingStation"
              placeholder="Select the Station"
              options={allStations}
              selectedValue={deviceDetails.chargingStation}
              valueRequired
              onChangeValue={handleDeviceDetailChange}
              error={!deviceDetails.chargingStation && formStatus.showFormErrors}
              errorMessage="Station name is required"
            />
            <CustomInput
              inputLabel="Device Name"
              type="text"
              placeholder="Enter the device name"
              name="chargingPointName"
              value={deviceDetails.chargingPointName}
              onChangeValue={handleDeviceDetailChange}
            />
            <CustomInput
              inputLabel="Model Name"
              type="text"
              placeholder="Enter the Model Name"
              name="chargePointModel"
              valueRequired
              value={deviceDetails.chargePointModel}
              onChangeValue={handleDeviceDetailChange}
              error={
                !stringValidation(1, 50, deviceDetails.chargePointModel) &&
                formStatus.showFormErrors
              }
              errorMessage="Model name is required"
            />
            <SelectFiled
              inputLabel="Model Series"
              options={deviceModelSeries}
              placeholder="Select model series"
              name="modelSeries"
              valueRequired
              selectedValue={deviceDetails.modelSeries}
              onChangeValue={handleDeviceDetailChange}
              error={!deviceDetails.modelSeries && formStatus.showFormErrors}
              errorMessage="Model series is required"
            />
            <CustomInputSelectBox
              disabled={deviceDetails.isInVLBC || deviceDetails.isDeviceOffline}
              inputLabel="Maximum Power Output (KW)"
              inputType="number"
              placeholder="Enter Value"
              inputName="maximumOutputPower"
              selectName="maximumOutputPowerUnit"
              valueRequired
              inputValue={deviceDetails.maximumOutputPower}
              selectValue={deviceDetails.maximumOutputPowerUnit}
              selectOptions={maxPowerOutputUnits}
              handleValueChange={handleDeviceDetailChange}
              error={
                !floatingNumberValidation(1, Infinity, deviceDetails.maximumOutputPower) &&
                formStatus.showFormErrors
              }
              errorMessage="Max power output is required and should be between 0 and 999."
            />
            <SelectFiled
              inputLabel="Connector Type"
              options={connectorTypes[currentBranding]}
              placeholder="Select connector type"
              name="connectorType"
              valueRequired
              selectedValue={deviceDetails.connectorType}
              onChangeValue={handleDeviceDetailChange}
              error={!deviceDetails.connectorType && formStatus.showFormErrors}
              errorMessage="Connector type is required"
            />
            <CustomInputSelectBox
              disabled={deviceDetails.isInVLBC || deviceDetails.isDeviceOffline}
              inputLabel="Specify the Desired Power Output"
              inputType="number"
              placeholder="Enter Value"
              inputName="desiredPowerOutput"
              selectName="powerUnit"
              inputValue={deviceDetails.desiredPowerOutput}
              handleValueChange={handleDeviceDetailChange}
              error={
                !floatingNumberValidation(1, Infinity, deviceDetails.desiredPowerOutput) &&
                formStatus.showFormErrors
              }
              errorMessage="Specify the Desired Power Output is required"
              selectValue={deviceDetails.powerUnit}
              selectOptions={desiredPowerOutputUnits}
            />
            <CustomInputSwitch
              disabled={deviceDetails.isInVLBC || deviceDetails.isDeviceOffline}
              inputLabel="PnC Mode (Only for JuiceBox)"
              value={deviceDetails.isJuiceBoxPncEnabled}
              name="isJuiceBoxPncEnabled"
              onChangeValue={handleDeviceDetailChange}
            />
            <CustomInputSwitch
              inputLabel="Use TxDefaultProfile (Only for JuiceBox)"
              value={deviceDetails.useTxDefaultProfile}
              name="useTxDefaultProfile"
              onChangeValue={handleDeviceDetailChange}
            />
          </div>
        </div>
        <div className="add-device-button-wrap">
          <div className="form-buttons-wrap">
            <MyButton label="Cancel" buttonType="back" onClickFunc={handleClickCancel} />
            <MyButton
              label="Submit"
              buttonType="submit"
              onClickFunc={handleSubmitDeviceDetails}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditDevice;
